<template>
  <div class="dashboard-live-preview">
    <div class="filters-row mb-4">
      <v-select
        v-model="selectedView"
        :items="navigationItems"
        label="Tela"
        hint="Views encontradas em navigation no YAML"
        persistent-hint
        dense
        style="max-width: 260px;"
      />
      <BaseDatePicker
        name-placeholder="Data de referência"
        :date-value="referenceDate"
        :max-width="140"
        dense
        locale="pt-br"
        time-zone="UTC"
        @on-input-date-picker="(value) => { referenceDate = value }"
      />
      <template v-for="filter in requiredExtraFilters">
        <BaseDatePicker
          v-if="filter.type === 'date'"
          :key="filter.name"
          :name-placeholder="filter.label"
          :date-value="filterValues[filter.name]"
          :max-width="140"
          dense
          locale="pt-br"
          time-zone="UTC"
          @on-input-date-picker="(value) => { filterValues[filter.name] = value }"
        />
        <v-text-field
          v-else
          :key="filter.name"
          v-model="filterValues[filter.name]"
          :label="filter.label"
          :type="filter.type === 'number' ? 'number' : 'text'"
          dense
          style="max-width: 200px;"
        />
      </template>
      <BaseButton
        type="text"
        color="primary"
        :is-disabled="!canSearch || isFetchingScreen"
        @click="searchScreen()"
      >
        Buscar
      </BaseButton>
    </div>

    <v-alert
      v-if="parseWarning"
      type="warning"
      dense
      text
    >
      {{ parseWarning }}
    </v-alert>

    <v-progress-linear
      v-if="isFetchingScreen"
      indeterminate
      color="primary"
      height="2"
      class="mt-4"
    />
    <v-divider class="mb-4" />
    <DashboardScreen
      v-if="currentScreen"
      :screen="currentScreen"
      @row-detail="onRowDetail"
      @input="onDrawerInput"
      @filter-change="onFilterChange"
      @update:options="onTableOptionsChange"
      @submit="onFilterSubmit"
    />

    <div
      v-else-if="!parseWarning && !isFetchingScreen"
      class="text-center grey--text text--darken-1 my-12"
    >
      {{ canSearch ? 'Nenhuma prévia disponível ainda — clique em "Buscar".' : 'Preencha os filtros obrigatórios e clique em "Buscar".' }}
    </div>

    <BaseDetailDrawer
      v-for="entry in openIndependentDrawersList"
      :key="entry.id"
      :value="true"
      :title="entry.row.detail.props.title || ''"
      :subtitle="entry.row.detail.props.subtitle || ''"
      :width="entry.row.detail.props.width"
      @input="(value) => onIndependentDrawerInput(entry.id, value)"
    >
      <DashboardNodeRenderer
        v-for="(child, index) in (entry.row.detail.children || [])"
        :key="child.id || index"
        :node="child"
        @row-detail="onRowDetail"
      />
    </BaseDetailDrawer>

    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>
import jsYaml from 'js-yaml'
import { mapActions } from 'vuex'
import DashboardScreen from '../../Dashboard/DashboardScreen.vue'
import DashboardNodeRenderer from '../../Dashboard/DashboardNodeRenderer.vue'
import BaseDetailDrawer from '../../Dashboard/BaseDetailDrawer.vue'
import BaseDatePicker from '../../BaseDatePicker'
import BaseSnackbar from '../../BaseSnackbar'
import BaseSnackbarConfiguration from '../../../mixins/BaseSnackbarConfiguration'
import DashboardIndependentDrawers from '../../../mixins/DashboardIndependentDrawers'

const AUTO_RESOLVED_PARAMS = ['program_id', 'reference_date']

function paramFromWhereValue(value) {
  return value && typeof value === 'object' && !Array.isArray(value) && value.param
    ? value.param
    : null
}

function paramsFromSelectFrom(fromExpression) {
  if (typeof fromExpression !== 'string') {
    return []
  }
  const matches = fromExpression.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || []
  return matches.map((match) => match.slice(1))
}

export default {
  name: 'DashboardLivePreview',
  components: { DashboardScreen, DashboardNodeRenderer, BaseDetailDrawer, BaseDatePicker, BaseSnackbar },
  mixins: [BaseSnackbarConfiguration, DashboardIndependentDrawers],
  props: {
    yamlContent: {
      type: String,
      required: true
    },
    programId: {
      type: String,
      required: true
    },
    configFileName: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      referenceDate: this.toLocalDate(new Date()).toISOString().substr(0, 10),
      navigationItems: [],
      selectedView: null,
      parseWarning: null,
      isFetchingScreen: false,
      baseScreenData: null,
      parsedYaml: null,
      filterValues: {}
    }
  },
  computed: {
    baseScreen() {
      return this.baseScreenData
    },
    requiredExtraFilters() {
      if (!this.parsedYaml || !this.selectedView) {
        return []
      }
      const view = (this.parsedYaml.views || []).find((v) => v.id === this.selectedView)
      if (!view) {
        return []
      }
      const dataDict = this.parsedYaml.data || {}
      const paramsDict = this.parsedYaml.params || {}
      const referenced = new Set()

      const scanDataBlock = (dataKey) => {
        const block = dataDict[dataKey]
        if (!block) {
          return
        }
        ;(block.where || []).forEach((clause) => {
          Object.keys(clause).forEach((key) => {
            if (key === 'field') {
              return
            }
            const paramName = paramFromWhereValue(clause[key])
            if (paramName) {
              referenced.add(paramName)
            }
          })
        })
        Object.values(block.select || {}).forEach((selectDef) => {
          paramsFromSelectFrom(selectDef && selectDef.from).forEach((paramName) => referenced.add(paramName))
        })
      }

      const scanContent = (contentList) => {
        (contentList || []).forEach((node) => {
          if (node.data) {
            scanDataBlock(node.data)
          }
          if (Array.isArray(node.content)) {
            scanContent(node.content)
          }
          if (Array.isArray(node.children)) {
            scanContent(node.children)
          }
        })
      }
      scanContent(view.content)

      return Array.from(referenced)
        .filter((name) => !AUTO_RESOLVED_PARAMS.includes(name))
        .filter((name) => paramsDict[name] && paramsDict[name].required !== false)
        .map((name) => ({
          name,
          label: paramsDict[name].label || name,
          type: paramsDict[name].type || 'string'
        }))
    },
    canSearch() {
      if (!this.selectedView || !this.referenceDate) {
        return false
      }
      return this.requiredExtraFilters.every((filter) => {
        const value = this.filterValues[filter.name]
        return value !== undefined && value !== null && String(value).trim() !== ''
      })
    }
  },
  watch: {
    selectedView(newValue, oldValue) {
      if (newValue === oldValue) {
        return
      }
      const values = {}
      this.requiredExtraFilters.forEach((filter) => { values[filter.name] = '' })
      this.filterValues = values
      this.baseScreenData = null
    }
  },
  mounted() {
    this.refreshPreview()
  },
  methods: {
    ...mapActions(['apiGetPlatformScreen']),
    refreshPreview() {
      this.parseYaml()
      if (this.canSearch) {
        this.fetchScreen()
      }
    },
    parseYaml() {
      this.parseWarning = null
      let parsed = null
      try {
        parsed = jsYaml.safeLoad(this.yamlContent)
      } catch (error) {
        this.parseWarning = 'Não foi possível interpretar o YAML.'
        this.parsedYaml = null
        this.navigationItems = []
        return
      }
      this.parsedYaml = parsed || {}
      const groups = Array.isArray(this.parsedYaml.navigation) ? this.parsedYaml.navigation : []
      const items = []
      groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          if (item && item.view && !items.some((existing) => existing.value === item.view)) {
            items.push({ text: item.view, value: item.view })
          }
        })
      })

      this.navigationItems = items
      if (!items.length) {
        this.parseWarning = 'Nenhuma view encontrada em "navigation" no YAML.'
      } else if (!this.selectedView || !items.some((item) => item.value === this.selectedView)) {
        this.selectedView = items[0].value
      }
    },
    searchScreen() {
      if (!this.canSearch) {
        return
      }
      this.fetchScreen()
    },
    async fetchScreen() {
      if (!this.selectedView) {
        return
      }
      try {
        this.isFetchingScreen = true
        const params = { reference_date: this.referenceDate, ...this.tableQueryParams }
        this.requiredExtraFilters.forEach((filter) => {
          params[filter.name] = this.filterValues[filter.name]
        })
        const result = await this.apiGetPlatformScreen({
          view: this.selectedView,
          programId: this.programId,
          spec: this.configFileName,
          params
        })
        this.baseScreenData = result
      } catch (error) {
        const missingParams = error.response && error.response.status === 400 && error.response.data && error.response.data.missingParams
        this.showFeedback({
          color: 'error',
          message: Array.isArray(missingParams) && missingParams.length
            ? `Parâmetros obrigatórios faltando: ${missingParams.join(', ')}.`
            : 'Erro ao buscar prévia da tela.'
        })
      } finally {
        this.isFetchingScreen = false
      }
    }
  }
}
</script>

<style scoped>
.filters-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
